/**
 * Which of a variant's two figures is the one to show and to buy.
 *
 * `applyVariantMoqRounding` (SLIM-439) hands every order line two numbers:
 *  - `quantity` — the unconstrained natural demand;
 *  - `moqQuantity` — the MOQ answer, after `minMoqThresholdPct` dropped the sizes (and possibly the
 *    whole variant) whose demand was too small to justify rounding up.
 *
 * The rule the whole NOOS buy flow agrees on: a variant commits either nothing, or at least its
 * MOQ. A MOQ is a floor, never a cap.
 *
 *  - MOQ answer at or above the natural demand → take it. This includes a variant the threshold
 *    zeroed outright: below "Minimum demand to round up to MOQ" it is not bought (SUP-417).
 *  - MOQ answer short but non-zero → take the natural demand. The dropped sizes outweighed the
 *    raises, but the variant already clears its MOQ, so the raw figure is both larger and valid.
 *
 * Resolved per variant, not per line, because the MOQ answer is one distribution for the whole
 * variant: it drops lines and then scales the survivors so the total lands exactly on the variant
 * MOQ. Taking the larger figure line by line would mix two distributions and overshoot the MOQ — a
 * variant suggesting 869 with a MOQ answer of 1.200 would be bought as 1.205 (SLIM-576).
 */
export type MoqOrderLine = {
  quantity: number
  moqQuantity?: number | null
}

export type MoqFigureSource = "moq" | "raw"

export function resolveMoqFigureSource(variantOrders: MoqOrderLine[]): MoqFigureSource {
  let rawTotal = 0
  let moqTotal = 0
  for (const order of variantOrders) {
    rawTotal += order.quantity
    // A null MOQ means the group has no MOQ configured, so that line's MOQ figure is its own demand.
    moqTotal += order.moqQuantity ?? order.quantity
  }
  return moqTotal >= rawTotal || moqTotal === 0 ? "moq" : "raw"
}

export function pickMoqFigure(order: MoqOrderLine, source: MoqFigureSource): number {
  switch (source) {
    case "moq":
      return order.moqQuantity ?? order.quantity
    case "raw":
      return order.quantity
  }
}

/** The lines a buy writes: each line's `quantity` becomes its variant's resolved figure. */
export function resolveVariantBuyQuantities<T extends MoqOrderLine & { skuId: string }>(
  lines: T[],
  variantIdOf: (skuId: string) => string | undefined,
): T[] {
  const keyOf = (line: T) => variantIdOf(line.skuId) ?? line.skuId
  const linesByVariant = new Map<string, T[]>()
  for (const line of lines) {
    const group = linesByVariant.get(keyOf(line))
    if (group) group.push(line)
    else linesByVariant.set(keyOf(line), [line])
  }
  const sourceByVariant = new Map(
    [...linesByVariant].map(([variantId, group]) => [variantId, resolveMoqFigureSource(group)]),
  )
  return lines.map((line) => ({
    ...line,
    quantity: pickMoqFigure(line, sourceByVariant.get(keyOf(line)) ?? "raw"),
  }))
}
